// Cards in the DOM (hand, decisions, inspect, showcase) show the very same face the 3D table uses:
// one Canvas2D renderer (scene/cardFaces.ts), drawn here at the displayed size. Small cards stay
// legible through the hover preview (CardPreview), which shows the face large.
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { create } from 'zustand';
import { COLORS, type Color, type DefId, type PlayerView } from '@bcm/rules';
import { C, values } from '../game/content';
import { devicePixels } from '../app/pixels';
import { backCanvas, faceCanvas, faceKind, problemResolutionOf, type BackKind, type SpellValues } from '../scene/cardFaces';

/** Cache key for a face: a City Problem's face depends on the Resolution it carries this game. */
const faceKey = (def: DefId, v: unknown) => `${def}:${JSON.stringify(v ?? null)}:${problemResolutionOf(def) ?? ''}`;

/** Permit / resource glyph. Shapes differ per school so colour is never the only signal. */
export function Glyph({ kind, title }: { kind: Color | 'mana' | 'vp'; title?: string }) {
  return <span className={`glyph glyph-${kind}`} role="img" aria-label={title ?? kind} title={title ?? kind} />;
}

const TOKEN: Record<string, Color | 'mana' | 'vp'> = { R: 'red', Y: 'yellow', B: 'blue', G: 'green', MANA: 'mana', VP: 'vp' };

/** Render rules text with {R}/{MANA}/… tokens as inline glyphs. */
export function RichText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const re = /\{([A-Z]+)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const kind = TOKEN[m[1]!];
    parts.push(kind ? <Glyph key={i++} kind={kind} /> : m[0]);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export function CostPips({ cost }: { cost: Partial<Record<Color, number>> }) {
  return (
    <span className="cost-pips">
      {COLORS.flatMap((c) => Array.from({ length: cost[c] ?? 0 }, (_, i) => <Glyph key={`${c}${i}`} kind={c} />))}
    </span>
  );
}

// ─── Face drawing ────────────────────────────────────────────────────────────

const scaled = new Map<string, HTMLCanvasElement>();

/** Downscale in halving steps (box-filter quality) so small cards keep crisp type. */
function scaledFace(key: string, src: HTMLCanvasElement, w: number, h: number): HTMLCanvasElement {
  const k = `${key}@${w}x${h}`;
  let out = scaled.get(k);
  if (out) return out;
  let cur: HTMLCanvasElement = src;
  while (cur.width / 2 >= w) {
    const next = document.createElement('canvas');
    next.width = Math.round(cur.width / 2);
    next.height = Math.round(cur.height / 2);
    const g = next.getContext('2d')!;
    g.imageSmoothingQuality = 'high';
    g.drawImage(cur, 0, 0, next.width, next.height);
    cur = next;
  }
  out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const g = out.getContext('2d')!;
  g.imageSmoothingQuality = 'high';
  g.drawImage(cur, 0, 0, w, h);
  if (scaled.size > 400) scaled.clear();
  scaled.set(k, out);
  return out;
}

export function FaceCanvas({ source, sourceKey }: { source: HTMLCanvasElement; sourceKey: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const draw = () => {
      const dpr = Math.min(2, devicePixels());
      const w = Math.max(1, Math.round(el.clientWidth * dpr));
      const h = Math.max(1, Math.round(el.clientHeight * dpr));
      if (el.width !== w || el.height !== h) {
        el.width = w;
        el.height = h;
      }
      const g = el.getContext('2d')!;
      g.clearRect(0, 0, w, h);
      g.drawImage(scaledFace(sourceKey, source, w, h), 0, 0);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
  }, [source, sourceKey]);
  return <canvas ref={ref} className="face" aria-hidden />;
}

// ─── Hover preview ───────────────────────────────────────────────────────────

interface PreviewState {
  def: DefId | null;
  values?: SpellValues;
  rect: DOMRect | null;
  show(def: DefId, values: SpellValues | undefined, rect: DOMRect): void;
  hide(): void;
}

let timer = 0;
export const usePreview = create<PreviewState>((set) => ({
  def: null,
  rect: null,
  show: (def, v, rect) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => set({ def, values: v, rect }), 220);
  },
  hide: () => {
    clearTimeout(timer);
    set({ def: null, rect: null });
  },
}));

/**
 * Touch: hover previews open on a tap instead (tap the same row again, or anywhere else, to close).
 * Spread onto an element that shows a preview on mouse hover.
 */
export function tapPreview(def: DefId, values?: SpellValues) {
  return {
    'data-preview-src': def,
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'touch') return;
      const p = usePreview.getState();
      if (p.def === def) p.hide();
      else {
        const rect = e.currentTarget.getBoundingClientRect();
        clearTimeout(timer);
        usePreview.setState({ def, values, rect });
      }
    },
  };
}

/** Large, readable view of the hovered card, placed beside it and kept on screen. */
export function CardPreview() {
  const { def, values: v, rect } = usePreview();
  // Touch: a tap outside the element that opened the preview closes it.
  useEffect(() => {
    if (!def) return;
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if ((e.target as HTMLElement | null)?.closest?.(`[data-preview-src="${def}"]`)) return;
      usePreview.getState().hide();
    };
    window.addEventListener('pointerdown', down, { capture: true });
    return () => window.removeEventListener('pointerdown', down, { capture: true });
  }, [def]);
  if (!def || !rect) return null;
  const src = faceCanvas(def, v);
  if (!src) return null;
  const landscape = src.width > src.height;
  const w = landscape ? 440 : 320;
  const h = Math.round((w * src.height) / src.width);
  let left = rect.left + rect.width / 2 - w / 2;
  let top = rect.top - h - 14;
  if (top < 64) {
    // Not enough room above: place beside the card instead.
    top = Math.min(innerHeight - h - 12, Math.max(64, rect.top + rect.height / 2 - h / 2));
    left = rect.right + 14 + w > innerWidth ? rect.left - w - 14 : rect.right + 14;
  }
  left = Math.max(8, Math.min(innerWidth - w - 8, left));
  return (
    <div className="card-preview" style={{ left, top, width: w }} aria-hidden>
      <div className="card-preview-face" style={{ height: h }}>
        <FaceCanvas source={src} sourceKey={`preview:${faceKey(def, v)}`} />
      </div>
    </div>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

interface CardProps {
  def: DefId;
  view?: PlayerView | null;
  size?: 'sm' | 'md' | 'lg';
  state?: 'playable' | 'dim' | 'selected' | 'used' | 'filed' | null;
  onClick?: (e?: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  anchor?: string;
  badge?: ReactNode;
  footer?: ReactNode;
  /** Show the large hover preview (off for already-large cards). */
  preview?: boolean;
}

function describe(def: DefId): string {
  const sp = C.spells.get(def);
  if (sp) return `${sp.name}. ${sp.text}`;
  const ac = C.actions.get(def);
  if (ac) return `${ac.name}. ${ac.text}`;
  const pr = C.problems.get(def);
  if (pr) {
    const res = C.resolutions.get(problemResolutionOf(def) ?? '');
    return `${pr.name}. ${pr.text}. ${pr.tiers[0]} / ${pr.tiers[1]} VP${res ? `. Resolution: ${res.name}, ${res.text}` : ''}`;
  }
  const rs = C.resolutions.get(def);
  if (rs) return `Resolution: ${rs.name}. ${rs.text}`;
  const id = C.identities.get(def);
  if (id) return `${id.name}, ${id.role}. ${id.abilityName}: ${id.text}`;
  const ag = C.agendas.get(def);
  if (ag) return `${ag.name}. ${ag.text}. ${ag.vp} VP`;
  const dc = C.decrees.get(def);
  if (dc) return `Decree: ${dc.name}. ${dc.text}`;
  return def;
}

export function Card({ def, view = null, size = 'md', state = null, onClick, onDoubleClick, anchor, badge, footer, preview }: CardProps) {
  const kind = faceKind(def);
  const v = kind === 'spell' ? values(def, view) : undefined;
  const src = faceCanvas(def, v);
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  const wantsPreview = preview ?? size !== 'lg';
  const landscape = kind === 'problem';
  const school = C.spells.get(def)?.school;
  const cls = `card card-${size} ${landscape ? 'landscape' : ''} ${state ? `is-${state}` : ''} ${onClick ? 'is-clickable' : ''}`;
  return (
    <div
      className={cls}
      data-anchor={anchor}
      data-kind={kind ?? undefined}
      data-school={school}
      role={onClick ? 'button' : 'img'}
      tabIndex={onClick ? 0 : undefined}
      aria-label={describe(def)}
      onClick={(e) => {
        hide();
        onClick?.(e);
      }}
      onDoubleClick={onDoubleClick}
      onKeyDown={(e) => onClick && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick())}
      onMouseEnter={(e) => wantsPreview && show(def, v, e.currentTarget.getBoundingClientRect())}
      onMouseLeave={hide}
    >
      {src && <FaceCanvas source={src} sourceKey={faceKey(def, v)} />}
      {badge}
      {footer}
    </div>
  );
}

export function CardBack({ kind = 'action', anchor }: { kind?: BackKind; anchor?: string }) {
  return (
    <div className={`card card-sm card-back ${kind === 'problem' ? 'landscape' : ''}`} data-anchor={anchor} aria-label={`${kind} card`}>
      <FaceCanvas source={backCanvas(kind)} sourceKey={`back:${kind}`} />
    </div>
  );
}
